import { Field, NativeSelect as ChakraNativeSelect } from "@chakra-ui/react";
import { forwardRef } from "react";
import { getThemeColor } from "theme/colors";

interface SelectItem {
  label: string;
  value: string;
  /**
   * Renders the option but refuses re-selection. Used to show a value the list no longer offers —
   * a retired model still bound to a route — which a native select would otherwise draw blank.
   */
  disabled?: boolean;
}

interface SelectProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
  placeholder?: string;
  items: SelectItem[];
  value?: string[];
  onValueChange?: (details: { value: string[] }) => void;
  w?: string;
  width?: string;
  size?: "xs" | "sm" | "md" | "lg";
  disabled?: boolean;
}

const nativeSelectFieldStyles = {
  bg: getThemeColor("input.bg"),
  borderColor: getThemeColor("input.border"),
  color: getThemeColor("input.text"),
  borderRadius: "md",
  _focus: {
    borderColor: getThemeColor("input.borderFocus"),
    boxShadow: `0 0 0 1px ${getThemeColor("input.borderFocus").base}`,
    _dark: {
      boxShadow: `0 0 0 1px ${getThemeColor("input.borderFocus")._dark}`,
    },
  },
};

// Native <option> elements are mostly OS-rendered, so this CSS only affects
// browsers that respect it (e.g. Firefox). Use brand tokens via CSS vars so
// nothing here drifts from the rest of the design system.
const optionStyles = `
  select option {
    background-color: var(--chakra-colors-bg-panel);
    color: var(--text-primary);
    padding: 8px 12px;
    font-size: 14px;
  }

  select option:hover {
    background-color: var(--bg-hover);
  }

  select option:checked {
    background: var(--chakra-colors-blue-solid) !important;
    color: #FFFFFF !important;
  }
`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      errorText,
      required,
      placeholder,
      items,
      value,
      onValueChange,
      w,
      width,
      size,
      disabled,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onValueChange) {
        onValueChange({ value: e.target.value ? [e.target.value] : [] });
      }
    };

    const selectWidth = w || width;

    if (label || helperText || errorText) {
      return (
        <>
          <style>{optionStyles}</style>
          <Field.Root invalid={!!errorText} required={required} w={selectWidth}>
            {label && <Field.Label>{label}</Field.Label>}
            <ChakraNativeSelect.Root size={size} disabled={disabled}>
              <ChakraNativeSelect.Field
                ref={ref}
                value={value?.[0] || ""}
                onChange={handleChange}
                {...nativeSelectFieldStyles}
                {...props}
              >
                {placeholder && <option value="">{placeholder}</option>}
                {items.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </option>
                ))}
              </ChakraNativeSelect.Field>
              <ChakraNativeSelect.Indicator />
            </ChakraNativeSelect.Root>
            {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
            {errorText && <Field.ErrorText>{errorText}</Field.ErrorText>}
          </Field.Root>
        </>
      );
    }

    return (
      <>
        <style>{optionStyles}</style>
        <ChakraNativeSelect.Root
          w={selectWidth}
          size={size}
          disabled={disabled}
        >
          <ChakraNativeSelect.Field
            ref={ref}
            value={value?.[0] || ""}
            onChange={handleChange}
            {...nativeSelectFieldStyles}
            {...props}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {items.map((item) => (
              <option
                key={item.value}
                value={item.value}
                disabled={item.disabled}
              >
                {item.label}
              </option>
            ))}
          </ChakraNativeSelect.Field>
          <ChakraNativeSelect.Indicator />
        </ChakraNativeSelect.Root>
      </>
    );
  },
);

Select.displayName = "Select";
